import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import DoctorShell from "@/components/DoctorShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
    MessageSquare, CheckCircle2, XCircle, RefreshCw,
    Sparkles, Clock, Users,
} from "lucide-react";

function RelativeTime({ iso }) {
    if (!iso) return <span className="text-xs text-slate-500">—</span>;
    const d = new Date(iso);
    const diff = Math.floor((Date.now() - d.getTime()) / 1000);
    const label = diff < 60 ? "just now"
        : diff < 3600 ? `${Math.floor(diff / 60)}m ago`
        : diff < 86400 ? `${Math.floor(diff / 3600)}h ago`
        : `${Math.floor(diff / 86400)}d ago`;
    return <span className="text-xs text-slate-500" title={d.toLocaleString()}>{label}</span>;
}

function DraftCard({ draft, onApprove, onReject, onEdit }) {
    const [editMode, setEditMode] = useState(false);
    const [q, setQ] = useState(draft.question);
    const [a, setA] = useState(draft.draft_answer);
    const [busy, setBusy] = useState(false);

    async function save() {
        setBusy(true);
        try {
            await onEdit(draft.id, { question: q, draft_answer: a });
            setEditMode(false);
        } finally { setBusy(false); }
    }

    return (
        <div className="rounded-2xl border border-[#E2E8F0] bg-white p-5"
            data-testid={`smart-faq-draft-${draft.id}`}>
            <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="secondary" className="bg-[#0F766E]/10 text-[#0F766E] border-0"
                        data-testid={`smart-faq-source-count-${draft.id}`}>
                        <Users size={11} className="mr-1" /> {draft.source_count} visitors asked this
                    </Badge>
                    <Badge variant="outline" className="text-xs">Draft</Badge>
                </div>
                <RelativeTime iso={draft.created_at} />
            </div>

            {editMode ? (
                <>
                    <label className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Question</label>
                    <Input value={q} onChange={(e) => setQ(e.target.value)} className="mt-1"
                        maxLength={500} data-testid={`smart-faq-edit-q-${draft.id}`} />
                    <label className="text-xs font-semibold text-[#64748B] uppercase tracking-wider mt-3 block">Answer</label>
                    <Textarea rows={4} value={a} onChange={(e) => setA(e.target.value)}
                        className="mt-1" maxLength={3000} data-testid={`smart-faq-edit-a-${draft.id}`} />
                </>
            ) : (
                <>
                    <div className="font-bold text-[#123B5D] text-[15px] leading-snug mb-2"
                        data-testid={`smart-faq-question-${draft.id}`}>
                        {draft.question}
                    </div>
                    <p className="text-[13.5px] text-[#334155] leading-relaxed whitespace-pre-wrap"
                        data-testid={`smart-faq-answer-${draft.id}`}>
                        {draft.draft_answer}
                    </p>
                </>
            )}

            <div className="flex items-center gap-2 mt-4 flex-wrap">
                {editMode ? (
                    <>
                        <Button size="sm" onClick={save} disabled={busy}
                            data-testid={`smart-faq-save-${draft.id}`}>
                            {busy ? "Saving…" : "Save edits"}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => {
                            setQ(draft.question); setA(draft.draft_answer); setEditMode(false);
                        }}>Cancel</Button>
                    </>
                ) : (
                    <>
                        <Button size="sm" onClick={() => onApprove(draft.id)}
                            data-testid={`smart-faq-approve-${draft.id}`}
                            className="bg-[#0F766E] hover:bg-[#115e59]">
                            <CheckCircle2 size={14} /> Approve &amp; publish
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setEditMode(true)}
                            data-testid={`smart-faq-edit-${draft.id}`}>
                            Edit before publishing
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => onReject(draft.id)}
                            data-testid={`smart-faq-reject-${draft.id}`}
                            className="text-rose-600 hover:text-rose-700">
                            <XCircle size={14} /> Reject
                        </Button>
                    </>
                )}
            </div>
        </div>
    );
}

export default function DoctorSmartFAQ() {
    const [doctor, setDoctor] = useState(null);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [runningNow, setRunningNow] = useState(false);

    async function load() {
        try {
            const [d, r] = await Promise.all([
                api.get("/me/doctor").catch(() => ({ data: null })),
                api.get("/me/b4/smart-faq/drafts"),
            ]);
            setDoctor(d.data);
            setData(r.data);
        } catch (e) {
            toast.error("Failed to load Smart FAQ queue");
        } finally { setLoading(false); }
    }
    useEffect(() => { load(); }, []);

    async function runNow() {
        setRunningNow(true);
        try {
            const r = await api.post("/me/b4/smart-faq/run-now");
            if (r.data?.drafts_created) {
                toast.success(`${r.data.drafts_created} new draft FAQ${r.data.drafts_created === 1 ? "" : "s"} created`);
            } else {
                toast(`No new drafts — needs at least 5 recent chat questions on new topics.`);
            }
            await load();
        } catch (e) {
            toast.error("Could not run clustering just now");
        } finally { setRunningNow(false); }
    }

    async function approve(id) {
        try {
            await api.post(`/me/b4/smart-faq/drafts/${id}/approve`);
            toast.success("Published to your FAQ library");
            await load();
        } catch (e) { toast.error("Approve failed"); }
    }
    async function reject(id) {
        if (!confirm("Reject this draft? It will not re-appear.")) return;
        try {
            await api.post(`/me/b4/smart-faq/drafts/${id}/reject`);
            toast.success("Rejected");
            await load();
        } catch (e) { toast.error("Reject failed"); }
    }
    async function edit(id, patch) {
        await api.patch(`/me/b4/smart-faq/drafts/${id}`, patch);
        toast.success("Draft updated");
        await load();
    }

    const pending = data?.pending || [];
    const recent = data?.recent_decided || [];

    return (
        <DoctorShell doctor={doctor}>
            <div className="max-w-5xl space-y-6" data-testid="smart-faq-page">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                        <h1 className="text-2xl font-bold text-[#123B5D] flex items-center gap-2">
                            <Sparkles size={22} className="text-[#0F766E]" />
                            Smart FAQ queue
                        </h1>
                        <p className="text-sm text-[#64748B] mt-1 max-w-2xl">
                            Each night, we cluster questions patients asked your chatbot and draft
                            FAQs you can approve. <strong>Nothing publishes without your approval.</strong>
                            Clustering only runs when at least {data?.min_new_messages_floor || 5} new
                            questions exist on new topics.
                        </p>
                    </div>
                    <Button onClick={runNow} disabled={runningNow} variant="outline"
                        data-testid="smart-faq-run-now">
                        <RefreshCw size={14} className={runningNow ? "animate-spin" : ""} />
                        {runningNow ? "Running…" : "Run clustering now"}
                    </Button>
                </div>

                {data?.last_run && (
                    <div className="rounded-xl border border-[#E5E9F2] bg-[#F8FAFC] p-4 flex items-center gap-6 flex-wrap text-xs text-[#475569]"
                        data-testid="smart-faq-last-run">
                        <div className="flex items-center gap-1.5"><Clock size={12} /> Last run: <RelativeTime iso={data.last_run.completed_at} /></div>
                        <div>Doctors scanned: <strong>{data.last_run.doctors_scanned}</strong></div>
                        <div>Drafts created: <strong>{data.last_run.drafts_created}</strong></div>
                        {data.last_run.errors?.length ? (
                            <div className="text-amber-700">Errors: {data.last_run.errors.length}</div>
                        ) : null}
                    </div>
                )}

                <Card className="border-[#E5E9F2] rounded-2xl shadow-none">
                    <CardHeader>
                        <CardTitle className="text-[#123B5D] text-lg flex items-center gap-2">
                            <MessageSquare size={18} /> Pending drafts ({pending.length})
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <div className="text-sm text-slate-500" data-testid="smart-faq-loading">Loading…</div>
                        ) : pending.length ? (
                            <div className="space-y-4" data-testid="smart-faq-pending-list">
                                {pending.map((d) => (
                                    <DraftCard key={d.id} draft={d}
                                        onApprove={approve} onReject={reject} onEdit={edit} />
                                ))}
                            </div>
                        ) : (
                            <div className="rounded-2xl border border-dashed border-[#CBD5E1] bg-[#F8FAFC] p-10 text-center"
                                data-testid="smart-faq-empty">
                                <Sparkles size={28} className="mx-auto text-[#0F766E] mb-3" />
                                <div className="font-semibold text-[#123B5D]">All caught up</div>
                                <p className="text-sm text-[#64748B] mt-1 max-w-md mx-auto">
                                    No pending drafts right now. We'll draft new ones automatically after
                                    visitors ask similar questions on your profile.
                                </p>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {recent.length > 0 && (
                    <Card className="border-[#E5E9F2] rounded-2xl shadow-none">
                        <CardHeader>
                            <CardTitle className="text-[#123B5D] text-lg">Recently decided</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-2" data-testid="smart-faq-recent-list">
                                {recent.map((r) => (
                                    <div key={r.id} className="flex items-center gap-3 py-2 border-b border-[#F1F5F9] last:border-0 text-sm"
                                        data-testid={`smart-faq-recent-${r.id}`}>
                                        <Badge variant={r.status === "approved" ? "default" : "secondary"}
                                            className={r.status === "approved" ? "bg-[#0F766E]" : "bg-slate-200 text-slate-700"}>
                                            {r.status}
                                        </Badge>
                                        <div className="flex-1 min-w-0 truncate text-[#17212B]">{r.question}</div>
                                        <RelativeTime iso={r.updated_at} />
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                )}
            </div>
        </DoctorShell>
    );
}
